import React, { useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Draggable } from 'react-beautiful-dnd';
import { Button, Checkbox, Icon } from 'semantic-ui-react';
import { usePopup } from '../../../lib/popup';

import NameEdit from './NameEdit';
// FAMILY-EXT: feature 5
import DescriptionEdit from './DescriptionEdit';
import ActionsStep from './ActionsStep';
import Linkify from '../../Linkify';

import styles from './Item.module.scss';

const Item = React.memo(
  ({ id, index, name, description, isCompleted, isPersisted, canEdit, onUpdate, onDelete }) => {
    const nameEdit = useRef(null);
    // FAMILY-EXT: feature 5
    const descriptionEdit = useRef(null);

    const handleClick = useCallback(() => {
      if (isPersisted && canEdit) {
        nameEdit.current.open();
      }
    }, [isPersisted, canEdit]);

    const handleNameUpdate = useCallback(
      (newName) => {
        onUpdate({
          name: newName,
        });
      },
      [onUpdate],
    );

    // FAMILY-EXT: feature 5
    const handleDescriptionUpdate = useCallback(
      (newDescription) => {
        onUpdate({
          description: newDescription,
        });
      },
      [onUpdate],
    );

    const handleToggleChange = useCallback(() => {
      onUpdate({
        isCompleted: !isCompleted,
      });
    }, [isCompleted, onUpdate]);

    const handleNameEdit = useCallback(() => {
      nameEdit.current.open();
    }, []);

    // FAMILY-EXT: feature 5
    const handleDescriptionEdit = useCallback(() => {
      descriptionEdit.current.open();
    }, []);

    // FAMILY-EXT: feature 5 — клик по описанию открывает редактор
    const handleDescriptionClick = useCallback(() => {
      if (isPersisted && canEdit) {
        descriptionEdit.current.open();
      }
    }, [isPersisted, canEdit]);

    const ActionsPopup = usePopup(ActionsStep);

    return (
      <Draggable draggableId={id} index={index} isDragDisabled={!isPersisted || !canEdit}>
        {({ innerRef, draggableProps, dragHandleProps }, { isDragging }) => {
          const contentNode = (
            // eslint-disable-next-line react/jsx-props-no-spreading
            <div {...draggableProps} {...dragHandleProps} ref={innerRef} className={styles.wrapper}>
              <span className={styles.checkboxWrapper}>
                <Checkbox
                  checked={isCompleted}
                  disabled={!isPersisted || !canEdit}
                  className={styles.checkbox}
                  onChange={handleToggleChange}
                />
              </span>
              <NameEdit ref={nameEdit} defaultValue={name} onUpdate={handleNameUpdate}>
                <div className={classNames(canEdit && styles.contentHoverable)}>
                  {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,
                                               jsx-a11y/no-static-element-interactions */}
                  <span
                    className={classNames(styles.text, canEdit && styles.textEditable)}
                    onClick={handleClick}
                  >
                    <span className={classNames(styles.task, isCompleted && styles.taskCompleted)}>
                      <Linkify linkStopPropagation>{name}</Linkify>
                    </span>
                  </span>
                  {isPersisted && canEdit && (
                    <ActionsPopup
                      onNameEdit={handleNameEdit}
                      onDescriptionEdit={handleDescriptionEdit}
                      onDelete={onDelete}
                    >
                      <Button className={classNames(styles.button, styles.target)}>
                        <Icon fitted name="pencil" size="small" />
                      </Button>
                    </ActionsPopup>
                  )}
                </div>
              </NameEdit>
              {/* FAMILY-EXT: feature 5 — отображение и инлайн-редактирование описания */}
              <DescriptionEdit
                ref={descriptionEdit}
                defaultValue={description || ''}
                onUpdate={handleDescriptionUpdate}
              >
                {description ? (
                  // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                  <div
                    className={classNames(
                      styles.description,
                      canEdit && styles.descriptionEditable,
                    )}
                    onClick={handleDescriptionClick}
                  >
                    <Linkify linkStopPropagation>{description}</Linkify>
                  </div>
                ) : (
                  <span />
                )}
              </DescriptionEdit>
            </div>
          );

          return isDragging ? ReactDOM.createPortal(contentNode, document.body) : contentNode;
        }}
      </Draggable>
    );
  },
);

Item.propTypes = {
  id: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  // FAMILY-EXT: feature 5
  description: PropTypes.string,
  isCompleted: PropTypes.bool.isRequired,
  isPersisted: PropTypes.bool.isRequired,
  canEdit: PropTypes.bool.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

Item.defaultProps = {
  description: null,
};

export default Item;
